import { Row } from "@tanstack/react-table";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button/button";
import { useCreateSalaryModal } from "../create-salary-modal/use-create-salary-modal";
import { useEditDateSheetModal } from "../edit-timesheet-modal/use-create-salary-modal";
import { editDatesheetByAdmin } from "@/config/mutation/admin";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog";
import { Trash } from "lucide-react";
import { useState } from "react";

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const key = Cookies.get("key") as string;
  const originData = row.original as {
    id: string;
    salaryGenerated: boolean;
    createdFromCSV: boolean;
  };
  const queryClient = useQueryClient();

  const { setSheetId, onOpenChange, setIsCreatedFromCsv } =
    useCreateSalaryModal();
  const { setSheetId: setEditSheetId, onOpenChange: onEditSheetModal } =
    useEditDateSheetModal();

  const { mutate: EditDatesheet, isPending } = useMutation({
    mutationFn: editDatesheetByAdmin(key),
  });

  const onDeletion = () => {
    EditDatesheet(
      {
        id: originData?.id as string,
        info: {
          deleted: true,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["timesheets-month"],
          });
          setIsOpen(false);

          toast.success("Salary Timesheet Deleted!");
        },
        onError: (error) => {
          console.log("ERROR WHILE DELETING DATESHEET", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex items-center gap-4">
      <Button
        size="sm"
        variant="default"
        onClick={() => {
          setSheetId(originData?.id);
          setIsCreatedFromCsv(originData?.createdFromCSV);
          onOpenChange(true);
        }}
        disabled={originData?.salaryGenerated}
      >
        {originData?.salaryGenerated ? "Salary Created" : "Create Salary"}
      </Button>

      {!originData?.salaryGenerated && !originData?.createdFromCSV && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setEditSheetId(originData?.id);
            onEditSheetModal(true);
          }}
          disabled={originData?.salaryGenerated}
        >
          Edit
        </Button>
      )}

      {!originData?.salaryGenerated && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button size="icon" variant="destructive" className="h-8 w-8">
              <Trash className="w-4 h-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Are you absolutely sure?</DialogTitle>
              <DialogDescription>
                This action cannot be undone. This will permanently delete this
                salary timesheet.
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center justify-end">
              <Button onClick={onDeletion} disabled={isPending}>
                Continue
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
